import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref, WritableComputedRef } from 'vue'

export type UnknownRecord = Record<string, unknown>

export type PersonLikeOption = {
  id: string | number
  label?: string
  full_name?: string
  name?: string
  phone?: string
  mobile?: string
}

export type PersonSelectOption = {
  id: string | number
  value: string | number
  label: string
  full_name: string
  name: string
  phone?: string
}

export type PendingUploadItem = {
  tempId: string
  file?: File
  tempUrl?: string
  base64?: boolean
  dataUrl?: string
  name?: string
}

export type PendingUploadsMap = Record<string, PendingUploadItem[]>

export type TemporaryMediaItem = {
  id: string | number
  url?: string
  file_name?: string
  name?: string
  type?: string
  uploading?: boolean
  failed?: boolean
}

export type PickerValueFormat = 'YYYY-MM-DD' | 'YYYY-MM-DD HH:mm:ss'

export function isDate (value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

export function parseISOToDate (value: unknown): Date | null {
  if (value == null || value === '') return null
  if (isDate(value)) return new Date(value.getTime())

  const normalized = String(value).replace(/\.(\d{3})\d+(?=Z|[+-]|$)/, '.$1')
  const parsed = new Date(normalized)
  return isDate(parsed) ? parsed : null
}

export function dateToISOString (value: Date | null): string | null {
  if (!value || !isDate(value)) return null
  return value.toISOString()
}

function padDatePart (value: number): string {
  return String(value).padStart(2, '0')
}

function formatDateToPickerValue (value: Date | null, format: PickerValueFormat): string | null {
  if (!value || !isDate(value)) return null

  const year = value.getFullYear()
  const month = padDatePart(value.getMonth() + 1)
  const day = padDatePart(value.getDate())

  if (format === 'YYYY-MM-DD') {
    return `${year}-${month}-${day}`
  }

  const hours = padDatePart(value.getHours())
  const minutes = padDatePart(value.getMinutes())
  const seconds = padDatePart(value.getSeconds())
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

function parsePickerValueToDate (value: string | null, format: PickerValueFormat): Date | null {
  if (!value) return null

  if (format === 'YYYY-MM-DD') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) return parseISOToDate(value)

    const [, year, month, day] = match
    const parsed = new Date(Number(year), Number(month) - 1, Number(day))
    return isDate(parsed) ? parsed : null
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/)
  if (!match) return parseISOToDate(value)

  const [, year, month, day, hours, minutes, seconds = '00'] = match
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds)
  )
  return isDate(parsed) ? parsed : null
}

export function normalizeAnswersForSnapshot (value: unknown): unknown {
  if (value == null) return value
  if (isDate(value)) return dateToISOString(value)
  if (Array.isArray(value)) return value.map(item => normalizeAnswersForSnapshot(item))
  if (typeof value === 'object') {
    const normalized: UnknownRecord = {}
    const keys = Object.keys(value as UnknownRecord).sort()
    for (const key of keys) {
      normalized[key] = normalizeAnswersForSnapshot((value as UnknownRecord)[key])
    }
    return normalized
  }
  return value
}

export function createSnapshot (value: unknown): string {
  return JSON.stringify(normalizeAnswersForSnapshot(value ?? {}))
}

export function hasMeaningfulValue (obj: UnknownRecord, key: string): boolean {
  if (!Object.prototype.hasOwnProperty.call(obj, key)) return false
  const value = obj[key]
  if (value == null) return false
  if (typeof value === 'boolean') return true
  if (isDate(value)) return true
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'object') return true
  return Boolean(value)
}

export function hasAnyMeaningfulValue (obj: UnknownRecord, keys: string[]): boolean {
  return keys.some((key) => hasMeaningfulValue(obj, key))
}

export function useRequiredFields (answers: ComputedRef<UnknownRecord>, requiredFields: string[], onChange: (value: boolean) => void): Ref<boolean> {
  const allRequiredFilled = ref(true)
  const requiredComputed = computed(() => requiredFields.every((field) => hasMeaningfulValue(answers.value, field)))

  watch(requiredComputed, (value) => {
    allRequiredFilled.value = value
    onChange(value)
  }, { immediate: true })

  return allRequiredFilled
}

export function useConditionalRequiredFields (
  getValue: () => boolean,
  onChange: (value: boolean) => void
): Ref<boolean> {
  const allRequiredFilled = ref(true)
  const requiredComputed = computed(() => getValue())

  watch(requiredComputed, (value) => {
    allRequiredFilled.value = value
    onChange(value)
  }, { immediate: true })

  return allRequiredFilled
}

export function useDirtySnapshot (answers: ComputedRef<UnknownRecord>, onDirty: (value: boolean) => void): {
  capture: (value?: unknown) => void
  originalSnapshot: Ref<string>
} {
  const originalSnapshot = ref('')

  watch(answers, (value) => {
    try {
      const current = createSnapshot(value)
      onDirty(originalSnapshot.value !== '' && current !== originalSnapshot.value)
    } catch {
      onDirty(true)
    }
  }, { deep: true })

  function capture (value: unknown = answers.value) {
    try {
      originalSnapshot.value = createSnapshot(value)
    } catch {
      originalSnapshot.value = ''
    }
    onDirty(false)
  }

  return { capture, originalSnapshot }
}

export function createDateProxy<T extends UnknownRecord, K extends keyof T> (
  answers: ComputedRef<T>,
  key: K,
  fallback: 'today' | null = null
): WritableComputedRef<Date | null> {
  return computed<Date | null>({
    get () {
      const value = answers.value[key]
      return parseISOToDate(value) ?? (fallback === 'today' ? new Date() : null)
    },
    set (value: Date | null) {
      answers.value[key] = dateToISOString(value) as T[K]
    }
  })
}

export function createDatePickerProxy<T extends UnknownRecord, K extends keyof T> (
  answers: ComputedRef<T>,
  key: K,
  format: PickerValueFormat,
  fallback: 'today' | null = null
): WritableComputedRef<string | null> {
  const dateProxy = createDateProxy(answers, key, fallback)

  return computed<string | null>({
    get () {
      return formatDateToPickerValue(dateProxy.value, format)
    },
    set (value: string | null) {
      dateProxy.value = parsePickerValueToDate(value, format)
    }
  })
}

export function getArrayField<T> (obj: UnknownRecord, key: string, fallback: T[] = []): T[] {
  const value = obj[key]
  return Array.isArray(value) ? value as T[] : fallback
}

export function ensureArrayField<T> (obj: UnknownRecord, key: string): T[] {
  if (!Array.isArray(obj[key])) obj[key] = []
  return obj[key] as T[]
}

export function ensureObjectField<T extends UnknownRecord> (obj: UnknownRecord, key: string): T {
  const value = obj[key]
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    obj[key] = {}
  }
  return obj[key] as T
}

export function ensureAnswersObject<T extends UnknownRecord> (value: T | unknown): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {} as T
  }
  return value as T
}

export function ensureAnswersMedia<T extends { answers?: unknown }> (form: T): UnknownRecord {
  form.answers = ensureAnswersObject(form.answers)
  return ensureObjectField(form.answers as UnknownRecord, 'media')
}

export function normalizeBooleanMap<T extends string> (value: unknown, keys: readonly T[]): Record<T, boolean> {
  const normalized = Object.fromEntries(keys.map((key) => [key, false])) as Record<T, boolean>

  if (Array.isArray(value)) {
    for (const entry of value) {
      const key = String(entry) as T
      if (key in normalized) normalized[key] = true
    }
    return normalized
  }

  if (value && typeof value === 'object') {
    for (const key of keys) {
      normalized[key] = Boolean((value as Record<string, unknown>)[key])
    }
  }

  return normalized
}

export function ensureBooleanMapField<T extends string> (obj: UnknownRecord, key: string, keys: readonly T[]): Record<T, boolean> {
  const normalized = normalizeBooleanMap(obj[key], keys)
  obj[key] = normalized
  return normalized
}

export function normalizePersonOption (person: PersonLikeOption): PersonSelectOption {
  const label = person.label ?? person.full_name ?? person.name ?? ''
  return {
    id: person.id,
    value: person.id,
    label,
    full_name: label,
    name: label,
    phone: person.phone ?? person.mobile ?? ''
  }
}

export function mapPeopleToOptions<T extends PersonLikeOption> (people: T[] = []): PersonSelectOption[] {
  return people
    .filter((person) => person != null && person.id != null)
    .map(normalizePersonOption)
    .filter((person) => person.label.trim().length > 0)
}

export function findPersonOptionByValue (options: PersonSelectOption[], value: string): PersonSelectOption | undefined {
  return options.find((option) => {
    return String(option.id) === value || String(option.phone ?? '') === value || option.label === value
  })
}

export function getStoredPersonSelectionValue (
  options: PersonSelectOption[],
  stored: { name?: unknown, id?: unknown, phone?: unknown }
): string | null {
  if (typeof stored.name === 'string' && stored.name.trim().length > 0) return stored.name
  if (stored.id != null) {
    const match = options.find(option => String(option.id) === String(stored.id))
    return match?.label ?? String(stored.id)
  }
  if (stored.phone != null) {
    const match = options.find(option => String(option.phone ?? '') === String(stored.phone))
    return match?.label ?? null
  }
  return null
}

export function filterOptionByLabel (input: string, option?: { label?: string }): boolean {
  return (option?.label ?? '').toLowerCase().includes((input ?? '').toLowerCase())
}

export function detectPersonLabelField (people: Array<Record<string, unknown>> = []): 'full_name' | 'name' {
  if (!people.length) return 'full_name'
  const first = people[0]
  if ('name' in first && !('full_name' in first)) return 'name'
  if ('full_name' in first) return 'full_name'
  if ('name' in first) return 'name'
  return 'full_name'
}

export function normalizeBooleanField (obj: UnknownRecord, key: string, fallback = false): boolean {
  const value = obj[key]
  const normalized = typeof value === 'boolean' ? value : fallback
  obj[key] = normalized
  return normalized
}

export function ensureNumberValue (value: unknown): number | null {
  if (value == null || value === '') return null
  const normalized = Number(value)
  return Number.isNaN(normalized) ? null : normalized
}

export function createTemporaryMediaItem (key: string, payload: File | { url: string, name?: string, file_name?: string }): {
  item: TemporaryMediaItem
  pending: PendingUploadItem
} {
  const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  if (payload instanceof File) {
    const tempUrl = URL.createObjectURL(payload)
    return {
      item: { id: tempId, url: tempUrl, file_name: payload.name, type: payload.type, uploading: true },
      pending: { tempId, file: payload, tempUrl }
    }
  }

  return {
    item: {
      id: tempId,
      url: payload.url,
      file_name: payload.name ?? payload.file_name ?? 'bestand',
      type: 'image'
    },
    pending: {
      tempId,
      base64: true,
      dataUrl: payload.url,
      name: payload.name ?? payload.file_name ?? `${key}.png`
    }
  }
}

export function queuePendingUpload (
  form: { answers?: unknown, _pendingUploads?: PendingUploadsMap },
  key: string,
  payload: File | { url: string, name?: string, file_name?: string }
): TemporaryMediaItem {
  const media = ensureAnswersMedia(form)
  const mediaList = ensureArrayField<TemporaryMediaItem>(media, key)
  const { item, pending } = createTemporaryMediaItem(key, payload)
  mediaList.push(item)

  if (!form._pendingUploads || typeof form._pendingUploads !== 'object') {
    form._pendingUploads = {}
  }
  if (!Array.isArray(form._pendingUploads[key])) {
    form._pendingUploads[key] = []
  }
  form._pendingUploads[key].push(pending)

  return item
}

export function isTemporaryMediaItem (value: unknown): value is TemporaryMediaItem {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const candidate = value as TemporaryMediaItem
  if (typeof candidate.id === 'string' && candidate.id.startsWith('tmp-')) return true
  if (typeof candidate.url === 'string' && candidate.url.startsWith('blob:')) return true
  return Boolean(candidate.uploading)
}

export function isPreviewReadyMediaItem (value: unknown): value is TemporaryMediaItem {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const candidate = value as TemporaryMediaItem
  const hasUrl = typeof candidate.url === 'string' && candidate.url.trim().length > 0
  const hasName = typeof candidate.file_name === 'string'
    ? candidate.file_name.trim().length > 0
    : typeof candidate.name === 'string' && candidate.name.trim().length > 0

  return hasUrl && hasName
}

function sanitizeMediaValueForSave (value: unknown): unknown {
  if (value instanceof File) return undefined
  if (typeof value === 'string') {
    return value.startsWith('blob:') ? undefined : value
  }

  if (Array.isArray(value)) {
    return value
      .map(item => sanitizeMediaValueForSave(item))
      .filter(item => item !== undefined)
  }

  if (isTemporaryMediaItem(value)) {
    return undefined
  }

  if (value && typeof value === 'object') {
    const normalized: UnknownRecord = {}
    for (const [key, entry] of Object.entries(value as UnknownRecord)) {
      const sanitized = sanitizeMediaValueForSave(entry)
      if (sanitized !== undefined) normalized[key] = sanitized
    }
    return normalized
  }

  return value
}

export function sanitizeAnswersForSave<T extends UnknownRecord> (answers: T): T {
  const normalized = ensureAnswersObject<T>(answers)
  const cloned = JSON.parse(JSON.stringify(normalized)) as T

  if (Object.prototype.hasOwnProperty.call(cloned, 'media')) {
    const sanitizedMedia = sanitizeMediaValueForSave((normalized as UnknownRecord).media)
    if (sanitizedMedia && typeof sanitizedMedia === 'object' && !Array.isArray(sanitizedMedia)) {
      (cloned as UnknownRecord).media = sanitizedMedia
    } else {
      delete (cloned as UnknownRecord).media
    }
  }

  return cloned
}

export function removeTemporaryMedia (
  form: { answers?: unknown, _pendingUploads?: PendingUploadsMap },
  tempId: string
): boolean {
  const media = ensureAnswersMedia(form)
  let removed = false

  for (const key of Object.keys(media)) {
    const list = media[key]
    if (!Array.isArray(list)) continue
    const index = list.findIndex((item) => String((item as TemporaryMediaItem).id) === tempId)
    if (index === -1) continue
    const [item] = list.splice(index, 1) as TemporaryMediaItem[]
    if (item?.url) {
      try { URL.revokeObjectURL(item.url) } catch {}
    }
    removed = true
  }

  if (form._pendingUploads && typeof form._pendingUploads === 'object') {
    for (const key of Object.keys(form._pendingUploads)) {
      const items = form._pendingUploads[key]
      if (!Array.isArray(items)) continue
      for (let index = items.length - 1; index >= 0; index--) {
        if (items[index]?.tempId !== tempId) continue
        if (items[index]?.tempUrl) {
          try { URL.revokeObjectURL(items[index].tempUrl as string) } catch {}
        }
        items.splice(index, 1)
        removed = true
      }
      if (!items.length) delete form._pendingUploads[key]
    }
    if (!Object.keys(form._pendingUploads).length) delete form._pendingUploads
  }

  return removed
}
